import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Account } from './accountType';
import AccountApi from './accountApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { getAccountIdFromSessionStorage } from '../lib/global_function';
import { CheckCircle, XCircle } from 'lucide-react';
import {
  validateEmail,
  validateLoginId,
  validatePasswordMatch,
  validateAndFormatPhoneNumber,
  validatePassword
} from '../lib/validator';
import HelpIcon from '../components/ui/helpIcon';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';

const UpdateAccountPage: React.FC = () => {
  const breadcrumbs = [
    { label: "Account", path: "/main/account/" },
    { label: "Update", path: "/main/account/update" },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const [account, setAccount] = useState<Account | null>(
    location.state?.account || null
  );

  const [formData, setFormData] = useState({
    first_name: account?.first_name || '',
    last_name: account?.last_name || '',
    phone: account?.phone || '',
    email: account?.email || '',
    login_id: account?.login_id || '',
    login_password: '',
    login_password_confirmation: '',
  });

  const [loading, setLoading] = useState(!account);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isGoogleAccount = account?.auth_provider === 'google';
  const [isUpdateSuccess, setIsUpdateSuccess] = useState(false);
  const [accountId, setAccountId] = useState<string>('');

  useEffect(() => {
    const fetchAccountIfNeeded = async () => {
      if (!account) {
        const accountIdFromSession = getAccountIdFromSessionStorage(
          process.env.REACT_APP_SESSION_STORAGE_KEY as string
        );

        if (!accountIdFromSession) {
          setErrorMessage('Account ID is missing.');
          setAccountId('');
          setLoading(false);
          return;
        }

        try {
          const fetchedAccount = await AccountApi.getAccount(accountIdFromSession);
          setAccount(fetchedAccount);
          setAccountId(fetchedAccount.account_id || '');
          setFormData({
            first_name: fetchedAccount.first_name || '',
            last_name: fetchedAccount.last_name || '',
            phone: fetchedAccount.phone || '',
            email: fetchedAccount.email || '',
            login_id: fetchedAccount.login_id || '',
            login_password: '',
            login_password_confirmation: '',
          });
        } catch (error) {
          setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
        } finally {
          setLoading(false);
        }
      } else {
        setAccountId(account.account_id || '');
      }
    };

    fetchAccountIfNeeded();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'phone': {
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setFormData((prevData) => ({ ...prevData, phone: phoneResult.value }));
        }
        break;
      }
      case 'email': {
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setFormData((prevData) => ({ ...prevData, email: emailResult.value }));
        }
        break;
      }
      case 'login_id': {
        const loginIdResult = validateLoginId(value);
        if (!loginIdResult.isValid) {
          errors.push(loginIdResult.error || 'Invalid login ID');
        } else {
          setFormData((prevData) => ({ ...prevData, login_id: loginIdResult.value }));
        }
        break;
      }
      case 'login_password': {
        const passwordResult = validatePassword(value);
        if (!passwordResult.isValid) {
          errors.push(passwordResult.error || 'Invalid password');
        } else {
          setFormData((prevData) => ({ ...prevData, login_password: passwordResult.value }));
        }
        break;
      }
      case 'login_password_confirmation': {
        const confirmResult = validatePasswordMatch(formData.login_password, value);
        if (!confirmResult.isValid) {
          errors.push(confirmResult.error || 'Passwords do not match');
        } else {
          setFormData((prevData) => ({ ...prevData, login_password_confirmation: confirmResult.value }));
        }
        break;
      }
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  const validateForm = (): boolean => {
    setErrorMessage('');
    const phoneResult = validateAndFormatPhoneNumber(formData.phone);
    if (!phoneResult.isValid) {
      setErrorMessage(phoneResult.error || 'Invalid phone number');
      return false;
    }

    if (isGoogleAccount) {
      return true;
    }

    const emailResult = validateEmail(formData.email);
    if (!emailResult.isValid) {
      setErrorMessage(emailResult.error || 'Invalid email address');
      return false;
    }

    const loginIdResult = validateLoginId(formData.login_id);
    if (!loginIdResult.isValid) {
      setErrorMessage(loginIdResult.error || 'Invalid login ID');
      return false;
    }

    const passwordResult = validatePassword(formData.login_password);
    if (!passwordResult.isValid) {
      setErrorMessage(passwordResult.error || 'Invalid password');
      return false;
    }

    const confirmResult = validatePasswordMatch(formData.login_password, formData.login_password_confirmation);
    if (!confirmResult.isValid) {
      setErrorMessage(confirmResult.error || 'Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setErrorMessage('');
    setIsUpdateSuccess(false);

    if (!validateForm()) {
      return;
    }

    try {
      const updatedAccount: Account = {
        ...account!,
        ...formData,
        login_password: isGoogleAccount ? '' : formData.login_password,
        login_password_confirmation: isGoogleAccount ? '' : formData.login_password_confirmation,
      };

      const finalAccountId = accountId || account?.account_id;
      if (!finalAccountId) {
        setErrorMessage('Account ID is missing to update the account.');
        return;
      }

      await AccountApi.update(finalAccountId, updatedAccount);
      setIsUpdateSuccess(true);
    } catch (error) {
      setIsUpdateSuccess(false);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  if (loading) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-bold text-foreground mt-6 mb-4">Update Online Account</h2>

        {errorMessage && (
          <Alert variant="error">
            {errorMessage}
          </Alert>
        )}

        {isUpdateSuccess ? (
          <Alert variant="success">
            Account updated successfully!
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="first_name" className="text-base">
                First Name: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="first_name"
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="last_name" className="text-base">
                Last Name: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="last_name"
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-base">
                Phone: <span className="text-destructive">*</span>
              </Label>
              <Input
                id="phone"
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="mt-2"
              />
            </div>

            {!isGoogleAccount && (
              <>
                <div>
                  <Label htmlFor="email" className="text-base">
                    Email: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="login_id" className="text-base">
                    Login ID: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="login_id"
                    type="text"
                    name="login_id"
                    value={formData.login_id}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="mt-2"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label htmlFor="login_password" className="text-base mb-0">
                      Password: <span className="text-destructive">*</span>
                    </Label>
                    <HelpIcon message="Password must be at least 8 characters long, including both lowercase and uppercase letters, at least one number, and at least one special character" />
                  </div>
                  <Input
                    id="login_password"
                    type="password"
                    name="login_password"
                    value={formData.login_password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                  />
                </div>

                <div>
                  <Label htmlFor="login_password_confirmation" className="text-base">
                    Confirm Password: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="login_password_confirmation"
                    type="password"
                    name="login_password_confirmation"
                    value={formData.login_password_confirmation}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="mt-2"
                  />
                </div>
              </>
            )}

            <div className="flex justify-start gap-2.5 mt-5">
              <Button type="submit" size="medium" variant="default">
                <CheckCircle className="size-5" />
                Update Account
              </Button>
              <Button
                type="button"
                size="medium"
                variant="inverted"
                onClick={() => navigate('/main/account')}
              >
                <XCircle className="size-5" />
                Cancel
              </Button>
            </div>
          </form>
        )}

        <div className="mb-[150px]"></div>
      </div>
    </>
  );
};

export default UpdateAccountPage;